import axios from "axios";
import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import { Container, Badge } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

import '../styles/Shop.css';
import { CountryContext } from "../context/CountryContext";
import StarRating from "../components/StarRating";
import BackToTop from "../components/BackToTop";
import WhatsappSupport from "../components/WhatsappSupport";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
const COMPANY_CODE = process.env.REACT_APP_COMPANY_CODE;

export default function Shop() {
  const { category: currentCategory } = useParams();
  const [styles, setStyles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannersLoading, setBannersLoading] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const carouselRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const autoplayRef = useRef(null);
  const interactionTimeoutRef = useRef(null);
  const [error, setError] = useState(null);
  const [exchangeRates, setExchangeRates] = useState({});
  const navigate = useNavigate();
  const [offerProducts, setOfferProducts] = useState([]);
  const [, setOfferLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [productSizes, setProductSizes] = useState({});
  const [loadingSizes, setLoadingSizes] = useState({});
  const [enableSizes, setEnableSizes] = useState(true);


  const currencySymbols = { US: '$', UK: '£', SL: 'LKR' };
  const { country } = useContext(CountryContext);

  // Fetch company feature settings
  useEffect(() => {
    const fetchFeatureSettings = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/company/get-feature-settings`, {
          params: { company_code: COMPANY_CODE }
        });
        setEnableSizes(response.data.enableSizes !== false);
      } catch (error) {
        console.error('Failed to fetch feature settings:', error);
        // Default to true if API fails
        setEnableSizes(true);
      }
    };
    fetchFeatureSettings();
  }, []);

  const getProductDetails = (styleNumber) => {
    navigate(`/product/${styleNumber}`);
  };

  // Fetch available sizes for a product
  const fetchProductSizes = useCallback(async (styleId) => {
    if (productSizes[styleId]) {
      // Already loaded
      return;
    }

    setLoadingSizes(prev => ({ ...prev, [styleId]: true }));
    try {
      const response = await axios.get(`${BASE_URL}/api/customer/style-variants/${styleId}`, {
        params: { company_code: COMPANY_CODE }
      });

      if (response.data && Array.isArray(response.data)) {
        // Extract unique sizes from variants
        const sizesSet = new Set();
        response.data.forEach(variant => {
          if (variant.size_name) {
            sizesSet.add(variant.size_name);
          }
        });
        
        const sizesList = Array.from(sizesSet).sort((a, b) => {
          // Sort sizes numerically if they are numbers, otherwise alphabetically
          const aNum = parseInt(a);
          const bNum = parseInt(b);
          if (!isNaN(aNum) && !isNaN(bNum)) {
            return aNum - bNum;
          }
          return a.localeCompare(b);
        });

        setProductSizes(prev => ({
          ...prev,
          [styleId]: sizesList
        }));
      }
    } catch (error) {
      console.error('Failed to fetch product sizes:', error);
      // Set empty array on error
      setProductSizes(prev => ({
        ...prev,
        [styleId]: []
      }));
    } finally {
      setLoadingSizes(prev => ({ ...prev, [styleId]: false }));
    }
  }, [productSizes]);

  // Fetch exchange rates
  useEffect(() => {
    const fetchExchangeRates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/customer/currency/rates`);
        if (response.data.success) {
          setExchangeRates(response.data.rates);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        setExchangeRates({
          GBP: 0.75,
          LKR: 320
        });
      }
    };
    fetchExchangeRates();
  }, []);

  // fetch offer products
  useEffect(() => {
    const fetchOfferProducts = async () => {
      try {
        setOfferLoading(true);

        const response = await axios.get(`${BASE_URL}/api/customer/offers`, {
          params: {company_code: COMPANY_CODE}
        });

        // Group products by style_id and select the best offer (minimum offer_price)
        const groupedProducts = {};
        response.data.forEach(product => {
          const styleId = product.style_id;
          
          if (!groupedProducts[styleId]) {
            groupedProducts[styleId] = product;
          } else {
            // If current product has lower offer_price, replace it
            if (product.offer_price < groupedProducts[styleId].offer_price) {
              groupedProducts[styleId] = product;
            }
          }
        });

        // Convert back to array
        const uniqueProducts = Object.values(groupedProducts);
        setOfferProducts(uniqueProducts);
      } catch (error) { 
        console.error('Error fetching offer products:', error);
      } finally{
        setOfferLoading(false);
      }
    };
    fetchOfferProducts();
  }, []);

  // Timer effect for real-time updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

    // Filter to show only active offers (started AND not ended)
  const activeOfferProducts = offerProducts.filter((p) => {
    // Check if offer has valid dates and offer_price
    if (!p.offer_start_date || !p.offer_end_date || Number(p.offer_price) <= 0) {
      return false;
    }
    
    const startDate = new Date(p.offer_start_date);
    const endDate = new Date(p.offer_end_date);
    
    // Offer must have started AND not yet ended
    return startDate <= currentTime && endDate > currentTime;
  });


  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      if (!COMPANY_CODE) {
        setError('Company code not configured');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/api/customer/main-categories`, {
          params: { company_code: COMPANY_CODE }
        });
        setCategories(response.data);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setError("Failed to load categories");
      }
    };
    fetchCategories();
  }, []);

  // Fetch banners when category changes
  useEffect(() => {
    const fetchBanners = async () => {
      if (!currentCategory || !COMPANY_CODE) {
        setBanners([]);
        return;
      }

      setBannersLoading(true);
      try {
        const response = await axios.get(
          `${BASE_URL}/api/customer/banners/category-name/${currentCategory}`,
          {
            params: { company_code: COMPANY_CODE }
          }
        );
        
        if (response.data.success) {
          setBanners(response.data.banners);
        } else {
          setBanners([]);
        }
      } catch (err) {
        console.error('Failed to fetch banners:', err);
        setBanners([]);
      } finally {
        setBannersLoading(false);
      }
    };

    fetchBanners();
  }, [currentCategory]);

  // Auto-advance and controls for banner carousel
  useEffect(() => {
    if (!carouselRef.current) return;

    const startAutoplay = () => {
      if (banners.length <= 1) return;
      if (autoplayRef.current) clearInterval(autoplayRef.current);
      autoplayRef.current = setInterval(() => {
        setCurrentBannerIndex(prev => (prev + 1) % banners.length);
      }, 5000);
    };

    const stopAutoplay = () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
        autoplayRef.current = null;
      }
    };

    if (!isPaused) {
      startAutoplay();
    } else {
      stopAutoplay();
    }

    return () => stopAutoplay();
  }, [banners]);

  // When index changes, scroll the target banner into view
  useEffect(() => {
    const el = carouselRef.current;
    if (!el || banners.length === 0) return;
    const index = Math.min(Math.max(0, currentBannerIndex), banners.length - 1);
    const child = el.children[index];
 if (child) {
      // Use scrollLeft for horizontal carousel without affecting vertical scroll
      const scrollAmount = index * child.offsetWidth;
      el.scrollTo({ left: scrollAmount, behavior: 'smooth' });
    }
  }, [currentBannerIndex, banners]);

  const goPrev = () => {
    if (!banners || banners.length === 0) return;
    setCurrentBannerIndex(i => (i - 1 + banners.length) % banners.length);
    setIsPaused(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setIsPaused(false), 8000);
  };

  const goNext = () => {
    if (!banners || banners.length === 0) return;
    setCurrentBannerIndex(i => (i + 1) % banners.length);
    setIsPaused(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setIsPaused(false), 8000);
  };

  // Fetch styles when category changes
  useEffect(() => {
    const fetchData = async () => {
      if (!currentCategory) {
        setLoading(false);
        return;
      }

      if (!COMPANY_CODE) {
        setError('Company code not configured');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (categories.length === 0) {
          return;
        }

        const matchedCategory = categories.find(
          cat => cat.category_name.toLowerCase() === currentCategory.toLowerCase()
        );

        if (!matchedCategory) {
          setError(`Category "${currentCategory}" not found`);
          setLoading(false);
          return;
        }

        const stylesResponse = await axios.get(
          `${BASE_URL}/api/customer/styles-by-parent-category/${matchedCategory.category_id}`,
          {
            params: { company_code: COMPANY_CODE }
          }
        );
        setStyles(stylesResponse.data);

      } catch (err) {
        console.error('Error fetching shop data:', err);
        setError('Failed to load styles');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentCategory, categories]);

  // Handle case where no category is specified
  useEffect(() => {
    const fetchAllStyles = async () => {
      if (currentCategory || categories.length === 0) return;

      if (!COMPANY_CODE) {
        setError('Company code not configured');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const stylesResponse = await axios.get(`${BASE_URL}/api/customer/all-styles`, {
          params: { company_code: COMPANY_CODE }
        });
        setStyles(stylesResponse.data);

      } catch (err) {
        console.error('Error fetching all styles:', err);
        setError('Failed to load styles');
      } finally {
        setLoading(false);
      }
    };

    fetchAllStyles();
  }, [currentCategory, categories]);

  const getRate = () => {
    switch (country) {
      case 'US':
        return 1;
      case 'UK':
        return exchangeRates['GBP'] || 0.75;
      case 'SL':
        return exchangeRates['LKR'] || 320;
      default:
        return 1;
    }
  };

   const formatPrice = (minPrice) => {
    if (!minPrice) return "Price not defined";
    const symbol = currencySymbols[country] || '$';
    const rate = getRate();
    const convertedPrice = (minPrice * rate).toFixed(2);
    return `${symbol}${convertedPrice}`;
  };

  
  if (loading) {
    return (
      <Container className="my-5">
        <div className="shop-loading-spinner">
          <div className="shop-spinner"></div>
          <p>Loading {currentCategory ? `${currentCategory} products` : 'products'}...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5">
        <div className="shop-error-message">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button 
            className="shop-btn btn-primary"
            onClick={() => navigate('/shop')}
          >
            Back to Shop
          </button>
        </div>
      </Container>
    );
  }

  return (
    <>
      {/* Dynamic Banner Section */}
      {currentCategory && (
        <div className="shop-banner mb-4">
          {bannersLoading ? (
            <div className="shop-banner-loading">
              <div className="shop-banner-skeleton"></div>
            </div>
          ) : banners.length > 0 ? (
            <div
              className="shop-banner-carousel-wrapper"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
              onFocus={() => setIsPaused(true)}
              onBlur={() => setIsPaused(false)}
              tabIndex={-1}
            >
              {banners.length > 1 && (
                <div className="shop-banner-controls">
                  <button
                    className="shop-banner-prev"
                    onClick={goPrev}
                    aria-label="Previous banner"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <button
                    className="shop-banner-next"
                    onClick={goNext}
                    aria-label="Next banner"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}

              <div className="shop-banner-carousel" ref={carouselRef}>
                {banners.map((banner, idx) => {
                  const filename =
                    banner.banner_url || banner.image_url || banner.imageUrl;
                  const key = banner.banner_id || banner.id || idx;
                  return (
                    <img
                      key={key}
                      src={`${BASE_URL}/uploads/banners/${filename}`}
                      className="shop-banner-img"
                      alt={banner.alt_text || `${currentCategory} banner`}
                    />
                  );
                })}
              </div>
            </div>
          ) : (
            // Fallback placeholder
            <div className="shop-banner-placeholder">
              <h2>
                {currentCategory.charAt(0).toUpperCase() +
                  currentCategory.slice(1)}{" "}
                Collection
              </h2>
              <p>Explore our exclusive range of {currentCategory} products!</p>
            </div>
          )}
        </div>
      )}

      {/* product grid */}
      <Container fluid className="my-5 shop-product-container">
        <h2 className="mb-4 text-capitalize">
          {currentCategory ? `${currentCategory} Collection` : "All Products"}
        </h2>

        {styles.length > 0 ? (
          <div className="shop-products-grid">
            {styles.map((product) => (
              <div
                key={product.style_id}
                className="shop-product-card"
                onClick={() => getProductDetails(product.style_number)}
                onMouseEnter={() => {
                  setHoveredProductId(product.style_id);
                  fetchProductSizes(product.style_id);
                }}
                onMouseLeave={() => setHoveredProductId(null)}
              >
                <div className="home-product-image-container">
                  <img
                    src={`${BASE_URL}/uploads/styles/${product.image}`}
                    alt={product.name}
                    className="home-product-image"
                  />

                  <div className="home-product-overlay">
                    <h5>Quick View</h5>
                  </div>

                  {/* Size Overlay - appears on hover */}
                  {enableSizes && (
                    <div 
                      className={`home-product-sizes-overlay ${
                        hoveredProductId === product.style_id ? 'show' : ''
                      }`}
                    >
                      <div className="sizes-content">
                        {/* <p className="sizes-label">Available Sizes:</p> */}
                        {loadingSizes[product.style_id] ? (
                          <div className="text-center py-2">
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          </div>
                        ) : (productSizes[product.style_id] && productSizes[product.style_id].length > 0) ? (
                          <div className="sizes-badges">
                            {productSizes[product.style_id].map((size, idx) => (
                              <Badge 
                                key={idx}
                                bg="light" 
                                text="dark"
                              >
                                {size}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-center text-muted small">No sizes available</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="shop-product-info">
                  <h3 className="shop-product-name">{product.name}</h3>
                  <p className="shop-product-description">
                    {product.description && product.description.length > 100
                      ? `${product.description.substring(0, 100)}...`
                      : product.description}
                  </p>

                  {/* Rating Display */}
                  {parseFloat(product.average_rating) > 0 &&
                    parseInt(product.review_count) > 0 && (
                      <div className="product-rating mb-2">
                        <StarRating
                          rating={Math.round(
                            parseFloat(product.average_rating)
                          )}
                          size="medium"
                        />
                        <span className="rating-text ms-2">
                          {parseFloat(product.average_rating).toFixed(1)}(
                          {parseInt(product.review_count)})
                        </span>
                      </div>
                    )}

                  <div className="shop-product-price">
                    {product.offer_price &&
                    product.offer_price !== 0 &&
                    activeOfferProducts.some(
                      (p) => p.style_id === product.style_id
                    ) ? (
                      <>
                        <span className="me-2">
                          {formatPrice(product.offer_price)}
                        </span>
                        <span className="text-muted text-decoration-line-through small">
                          {formatPrice(product.sale_price)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>{formatPrice(product.sale_price)}</span>
                      </>
                    )}
                  </div>

                  {/* {product.category_name && (
                    <div className="shop-product-category">
                      <span className="shop-category-badge">
                        {product.category_name}
                      </span>
                    </div>
                  )} */}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="shop-no-products">
            <div className="shop-no-products-content">
              <i className="fas fa-search fa-3x"></i>
              <h3>No products found</h3>
              <p>
                No products found
                {currentCategory ? ` in ${currentCategory} category` : ""}.
              </p>
            </div>
          </div>
        )}
      </Container>

      <BackToTop />
      <WhatsappSupport message="Hi, I need help with your products!" />
    </>
  );
}